import { motion, useReducedMotion } from "motion/react";
import { PaymentCard } from "@/features/card/components/CardManager/PaymentCard/PaymentCard";
import { PaymentCardVerso } from "@/features/card/components/CardManager/PaymentCard/PaymentCardVerso";
import { ONBOARDING_CARD_MOTION } from "@/features/card/configs/card.config";
import { EnhancedCard } from "@/features/card/types/card.type";
import { cn } from "@/shared/utils/style.util";

const SHEEN_CLASS = "absolute inset-0 overflow-hidden rounded-md pointer-events-none";
const SHEEN_GRADIENT = "size-full bg-gradient-to-r from-transparent via-white/30 to-transparent dark:via-white/20";

type OnboardingCardProps = {
  card: EnhancedCard
  isFlipped: boolean
  hasActivationError?: boolean
  hasSheen?: boolean
};

export const OnboardingCard = ({
  card,
  isFlipped,
  hasActivationError = false,
  hasSheen = false,
}: OnboardingCardProps) => {
  const reduceMotion = useReducedMotion();

  return (
    <div
      className={cn(
        "flex justify-start items-center my-8 md:my-16 short:!my-2",
        hasActivationError && "animate-shake",
      )}
    >
      <div className="relative">
        <div className="relative drop-shadow-2xl perspective-1000 animate-float">
          <div
            className={cn(
              "w-[310px] aspect-[287/168] transition-transform duration-700 transform-style-preserve-3d",
              isFlipped && "rotate-y-180 sm:scale-125 short:!scale-100",
            )}
          >
            {/* Recto */}
            <div className="absolute w-full h-full backface-hidden">
              <PaymentCard
                dataTestId="payment-card-activating"
                data={{
                  ...card,
                  isActive: true,
                }}
              />
            </div>

            {/* Verso */}
            <div className="absolute w-full h-full backface-hidden rotate-y-180">
              <PaymentCardVerso data={card} />
            </div>
          </div>
        </div>
        {hasSheen && !reduceMotion && (
          <div className={SHEEN_CLASS} aria-hidden="true" data-testid="card-sheen">
            <motion.div
              className={SHEEN_GRADIENT}
              initial={{ x: "-100%" }}
              animate={{ x: "100%" }}
              transition={ONBOARDING_CARD_MOTION.sheen}
            />
          </div>
        )}
      </div>
    </div>
  );
};
